"use client";

import { BrandLockup } from "@/components/brand/BrandLockup";
import { DemoVideo } from "@/components/landing/DemoVideo";
import { SpeakingDemoIframe } from "@/components/seo/SpeakingDemoIframe";
import { AFFILIATE_PROGRAM } from "@/lib/affiliate-program";
import {
  AFFILIATE_RECRUITMENT_AUDIENCES,
  AFFILIATE_RECRUITMENT_CHANNELS,
  AFFILIATE_RECRUITMENT_STEPS,
  type AffiliateRecruitmentAudienceId,
  type AffiliateRecruitmentChannelId,
  getAffiliateStarterPlan,
  isAffiliateRecruitmentAudienceId,
  isAffiliateRecruitmentChannelId,
} from "@/lib/affiliate-recruitment-onboarding";
import { trackEventWithAttribution } from "@/lib/analytics/posthog-helpers";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  Check,
  CirclePlay,
  GraduationCap,
  Languages,
  Megaphone,
  MessageCircleMore,
  Mic,
  Newspaper,
  PlaneTakeoff,
  UsersRound,
  Video,
} from "lucide-react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { type ComponentType, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "chickytutor_affiliate_recruitment_onboarding_v2";
const DASHBOARD_HREF = "/affiliate/dashboard";

type StoredProgress = {
  version: 2;
  stepIndex: number;
  audienceId: AffiliateRecruitmentAudienceId | null;
  channelId: AffiliateRecruitmentChannelId | null;
};

type IconComponent = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

const AUDIENCE_ICONS: Record<AffiliateRecruitmentAudienceId, IconComponent> = {
  language_learners: Languages,
  students_between_lessons: GraduationCap,
  travel_global: PlaneTakeoff,
  ai_education: BookOpenText,
};

const CHANNEL_ICONS: Record<AffiliateRecruitmentChannelId, IconComponent> = {
  video_social: Video,
  newsletter_blog: Newspaper,
  class_community: UsersRound,
  paid_ads: Megaphone,
};

const stepNames = ["Start", "Your audience", "Your channel", "Product demo", "Your plan"];

const primaryButtonClassName =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#FD954F] px-6 py-3 font-black text-[#1C1C1E] shadow-[0_12px_30px_rgba(192,92,47,0.22)] transition hover:-translate-y-0.5 hover:bg-[#F28747] active:translate-y-px focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FD954F]/35 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0";

function ChoiceButton({
  selected,
  icon: Icon,
  label,
  description,
  onClick,
}: {
  selected: boolean;
  icon: IconComponent;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`group grid min-h-32 w-full grid-cols-[auto_1fr_auto] items-start gap-4 rounded-2xl border p-5 text-left transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FD954F]/30 sm:p-6 ${
        selected
          ? "border-[#C05C2F] bg-[#FFF1E6] shadow-[0_12px_30px_rgba(192,92,47,0.1)]"
          : "border-[#1C1C1E]/12 bg-white hover:-translate-y-0.5 hover:border-[#C05C2F]/45 hover:shadow-[0_12px_28px_rgba(28,28,30,0.08)]"
      }`}
    >
      <span
        className={`flex h-11 w-11 items-center justify-center rounded-full ${
          selected ? "bg-[#FD954F] text-[#1C1C1E]" : "bg-[#E9EFE7] text-[#46624C]"
        }`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <span>
        <span className="block text-lg font-black tracking-[-0.02em]">{label}</span>
        <span className="mt-1 block leading-6 text-[#626864]">{description}</span>
      </span>
      <span
        className={`mt-1 flex h-6 w-6 items-center justify-center rounded-full border ${
          selected
            ? "border-[#C05C2F] bg-[#C05C2F] text-white"
            : "border-[#1C1C1E]/20 text-transparent"
        }`}
        aria-hidden="true"
      >
        <Check className="h-4 w-4" />
      </span>
    </button>
  );
}

export function AffiliateOnboardingFlow() {
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const startedRef = useRef(false);
  const autoAdvanceTimerRef = useRef<number | null>(null);
  const liveDemoOpenedAtRef = useRef<number | null>(null);
  const liveDemoLoadedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [audienceId, setAudienceId] = useState<AffiliateRecruitmentAudienceId | null>(null);
  const [channelId, setChannelId] = useState<AffiliateRecruitmentChannelId | null>(null);
  const [showLiveDemo, setShowLiveDemo] = useState(false);
  const [hasOpenedLiveDemo, setHasOpenedLiveDemo] = useState(false);

  const stepId = AFFILIATE_RECRUITMENT_STEPS[stepIndex];
  const plan = useMemo(
    () => (audienceId && channelId ? getAffiliateStarterPlan(audienceId, channelId) : null),
    [audienceId, channelId]
  );

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<StoredProgress>;
        const savedAudienceId = isAffiliateRecruitmentAudienceId(parsed.audienceId)
          ? parsed.audienceId
          : null;
        const savedChannelId =
          savedAudienceId && isAffiliateRecruitmentChannelId(parsed.channelId)
            ? parsed.channelId
            : null;

        setAudienceId(savedAudienceId);
        setChannelId(savedChannelId);

        if (
          parsed.version === 2 &&
          Number.isInteger(parsed.stepIndex) &&
          Number(parsed.stepIndex) >= 0 &&
          Number(parsed.stepIndex) < AFFILIATE_RECRUITMENT_STEPS.length
        ) {
          let safeStepIndex = Number(parsed.stepIndex);
          if (safeStepIndex >= 2 && !savedAudienceId) safeStepIndex = 1;
          if (safeStepIndex >= 3 && !savedChannelId) safeStepIndex = 2;
          setStepIndex(safeStepIndex);
        }
      }
    } catch {
      // Continue from the first step when storage is unavailable or malformed.
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hydrated) return;

    const progress: StoredProgress = {
      version: 2,
      stepIndex,
      audienceId,
      channelId,
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch {
      // The flow still works when browser storage is unavailable.
    }
  }, [audienceId, channelId, hydrated, stepIndex]);

  useEffect(() => {
    if (!hydrated) return;

    if (!startedRef.current) {
      startedRef.current = true;
      trackEventWithAttribution(posthog, "affiliate_recruitment_onboarding_started", {
        resumed: stepIndex > 0,
      });
    }

    trackEventWithAttribution(posthog, "affiliate_recruitment_onboarding_step_viewed", {
      step_id: stepId,
      step_number: stepIndex + 1,
    });
  }, [hydrated, posthog, stepId, stepIndex]);

  useEffect(() => {
    if (!hydrated) return;
    headingRef.current?.focus();
  }, [hydrated, showLiveDemo, stepIndex]);

  useEffect(
    () => () => {
      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
    },
    []
  );

  const scheduleAutoAdvance = (nextStepIndex: number) => {
    if (autoAdvanceTimerRef.current !== null) {
      window.clearTimeout(autoAdvanceTimerRef.current);
    }
    autoAdvanceTimerRef.current = window.setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      setStepIndex(nextStepIndex);
    }, 180);
  };

  const selectAudience = (value: AffiliateRecruitmentAudienceId) => {
    setAudienceId(value);
    trackEventWithAttribution(posthog, "affiliate_recruitment_onboarding_choice_selected", {
      step_id: "audience",
      choice_id: value,
    });
    scheduleAutoAdvance(2);
  };

  const selectChannel = (value: AffiliateRecruitmentChannelId) => {
    setChannelId(value);
    trackEventWithAttribution(posthog, "affiliate_recruitment_onboarding_choice_selected", {
      step_id: "channel",
      choice_id: value,
    });
    scheduleAutoAdvance(3);
  };

  const goForward = () => {
    if (stepId === "demo" && !hasOpenedLiveDemo) {
      trackEventWithAttribution(posthog, "affiliate_recruitment_live_demo_skipped", {
        step_id: "demo",
      });
    }
    setStepIndex((current) => Math.min(current + 1, AFFILIATE_RECRUITMENT_STEPS.length - 1));
  };

  const goBack = () => {
    setStepIndex((current) => Math.max(current - 1, 0));
  };

  const restart = () => {
    setAudienceId(null);
    setChannelId(null);
    setShowLiveDemo(false);
    setHasOpenedLiveDemo(false);
    setStepIndex(0);
  };

  const openLiveDemo = () => {
    liveDemoOpenedAtRef.current = Date.now();
    liveDemoLoadedRef.current = false;
    setHasOpenedLiveDemo(true);
    setShowLiveDemo(true);
    trackEventWithAttribution(posthog, "affiliate_recruitment_live_demo_opened", {
      placement: "affiliate_onboarding_demo_step",
    });
  };

  const closeLiveDemo = () => {
    const openedAt = liveDemoOpenedAtRef.current;
    liveDemoOpenedAtRef.current = null;
    setShowLiveDemo(false);
    trackEventWithAttribution(posthog, "affiliate_recruitment_live_demo_closed", {
      duration_seconds: openedAt ? Math.max(0, Math.round((Date.now() - openedAt) / 1000)) : 0,
    });
  };

  if (!hydrated) {
    return (
      <main className="min-h-[100dvh] bg-[#F5F5F0] text-[#1C1C1E]">
        <div className="mx-auto flex min-h-[100dvh] max-w-5xl items-center px-5 py-12 sm:px-8">
          <div className="w-full animate-pulse" aria-label="Loading affiliate onboarding">
            <div className="h-8 w-44 rounded-full bg-[#1C1C1E]/10" />
            <div className="mt-12 h-14 max-w-xl rounded-2xl bg-[#1C1C1E]/10" />
            <div className="mt-5 h-6 max-w-2xl rounded-full bg-[#1C1C1E]/10" />
            <div className="mt-10 h-72 rounded-3xl bg-[#1C1C1E]/10" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-[100dvh] overflow-hidden bg-[#F5F5F0] text-[#1C1C1E]">
      <header className="border-b border-[#1C1C1E]/10 bg-[#F5F5F0]/95 backdrop-blur-md">
        <div className="mx-auto flex min-h-[72px] max-w-6xl items-center justify-between gap-5 px-5 sm:px-8">
          <Link href="/" aria-label="ChickyTutor home">
            <BrandLockup surface="light" className="h-auto w-[142px]" priority />
          </Link>
          <Link
            href="/affiliate"
            className="text-sm font-bold text-[#5E625F] transition hover:text-[#1C1C1E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C05C2F]"
          >
            Back to program
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 pb-10 pt-6 sm:px-8 sm:pb-14 sm:pt-8">
        <div
          className="flex items-center gap-4"
          aria-label={`Onboarding progress: ${stepIndex + 1} of 5`}
        >
          <ol className="flex flex-1 gap-2" aria-hidden="true">
            {AFFILIATE_RECRUITMENT_STEPS.map((step, index) => (
              <li
                key={step}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  index <= stepIndex ? "bg-[#C05C2F]" : "bg-[#1C1C1E]/12"
                }`}
              />
            ))}
          </ol>
          <span className="min-w-28 text-right text-sm font-bold text-[#626864]">
            {stepNames[stepIndex]}
          </span>
        </div>

        <p className="sr-only" aria-live="polite">
          {stepNames[stepIndex]}, {stepIndex + 1} of {AFFILIATE_RECRUITMENT_STEPS.length}
        </p>

        <AnimatePresence mode="wait" initial={false}>
          <motion.section
            key={stepId}
            initial={reduceMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: reduceMotion ? 0 : 0.24 }}
            className="min-h-[calc(100dvh-15rem)] py-8 sm:py-12"
          >
            {stepId === "welcome" && (
              <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-14">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C05C2F]">
                    Affiliate setup
                  </p>
                  <h1
                    ref={headingRef}
                    tabIndex={-1}
                    className="mt-4 max-w-[12ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                  >
                    Set up your affiliate link.
                  </h1>
                  <p className="mt-5 max-w-xl text-lg leading-8 text-[#5E625F]">
                    Answer two quick questions, see what learners experience, and get a promotion
                    plan before you create your account.
                  </p>
                  <button
                    type="button"
                    onClick={goForward}
                    className={`${primaryButtonClassName} mt-7`}
                  >
                    Start setup
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                  <p className="mt-3 text-sm font-semibold text-[#6A706C]">
                    About two minutes to your plan
                  </p>
                </div>

                <aside className="rounded-3xl bg-[#1C1C1E] p-6 text-white shadow-[0_24px_60px_rgba(28,28,30,0.16)] sm:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FD954F]">
                    What happens next
                  </p>
                  <ol className="mt-6 space-y-5">
                    {[
                      "Choose the audience you understand best",
                      "Choose where you plan to share ChickyTutor",
                      "Review the product and a tailored promotion angle",
                    ].map((item, index) => (
                      <li key={item} className="flex items-start gap-4">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FD954F] text-sm font-black text-[#1C1C1E]">
                          {index + 1}
                        </span>
                        <span className="pt-1 text-sm font-semibold leading-6 text-white/80">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-7 border-t border-white/15 pt-5">
                    <p className="text-sm leading-6 text-white/65">
                      New to the programme? Affiliates earn 15% of eligible subscription revenue
                      during a referred learner&apos;s first 12 months.
                    </p>
                    <Link
                      href="/affiliate"
                      className="mt-3 inline-flex min-h-10 items-center font-bold text-[#FD954F] underline decoration-[#FD954F]/35 underline-offset-4 hover:text-[#F28747]"
                    >
                      View full programme details
                    </Link>
                  </div>
                </aside>
              </div>
            )}

            {stepId === "audience" && (
              <div className="mx-auto max-w-4xl">
                <h1
                  ref={headingRef}
                  tabIndex={-1}
                  className="max-w-[12ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                >
                  Who are you helping?
                </h1>
                <p className="mt-4 max-w-xl text-lg leading-8 text-[#5E625F]">
                  Choose the group that sounds most like your audience.
                </p>
                <div className="mt-8 grid gap-4 md:grid-cols-2">
                  {AFFILIATE_RECRUITMENT_AUDIENCES.map((audience) => (
                    <ChoiceButton
                      key={audience.id}
                      selected={audienceId === audience.id}
                      icon={AUDIENCE_ICONS[audience.id]}
                      label={audience.label}
                      description={audience.description}
                      onClick={() => selectAudience(audience.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {stepId === "channel" && (
              <div className="mx-auto max-w-4xl">
                <h1
                  ref={headingRef}
                  tabIndex={-1}
                  className="max-w-[12ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                >
                  Where do they hear from you?
                </h1>
                <p className="mt-4 max-w-xl text-lg leading-8 text-[#5E625F]">
                  Pick the channel you would use first. You can add more later.
                </p>
                <div className="mt-8 grid gap-4 md:grid-cols-2">
                  {AFFILIATE_RECRUITMENT_CHANNELS.map((channel) => (
                    <ChoiceButton
                      key={channel.id}
                      selected={channelId === channel.id}
                      icon={CHANNEL_ICONS[channel.id]}
                      label={channel.label}
                      description={channel.description}
                      onClick={() => selectChannel(channel.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {stepId === "demo" && !showLiveDemo && (
              <div className="mx-auto max-w-5xl">
                <div className="grid gap-7 lg:grid-cols-[0.72fr_1.28fr] lg:items-end">
                  <div>
                    <CirclePlay className="h-10 w-10 text-[#C05C2F]" aria-hidden />
                    <h1
                      ref={headingRef}
                      tabIndex={-1}
                      className="mt-5 max-w-[11ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                    >
                      See what learners experience.
                    </h1>
                    <p className="mt-5 max-w-lg text-lg leading-8 text-[#5E625F]">
                      Watch the walkthrough, then try a real conversation if you want.
                    </p>
                  </div>
                  <div className="space-y-3 border-l-2 border-[#C05C2F] pl-5 text-[#525A55]">
                    <p className="font-bold text-[#1C1C1E]">Listen for these useful details:</p>
                    <p>Real-time voice conversation</p>
                    <p>Prompts, hints, and corrections during practice</p>
                    <p>Speaking practice across 70+ languages</p>
                  </div>
                </div>
                <div className="mt-9 overflow-hidden rounded-3xl border border-[#1C1C1E]/10 bg-[#0F0F14] shadow-[0_24px_60px_rgba(28,28,30,0.18)]">
                  <DemoVideo
                    src="/ui/chickytutor-how-it-works4.mp4"
                    poster="/ui/chickytutor-how-it-works4-thumbnail.jpg"
                    appearance="brand"
                    analyticsContext={{
                      demoId: "affiliate_onboarding_product_demo_v1",
                      demoKind: "product_video",
                      pagePath: "/affiliateonboarding",
                      pageFamily: "affiliate_program",
                      placement: "onboarding_product_demo",
                    }}
                  />
                </div>

                <div className="mt-6 grid gap-5 rounded-3xl border border-[#1C1C1E]/10 bg-[#E9EFE7] p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:p-7">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#46624C] text-white">
                    <Mic className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <p className="text-sm font-black text-[#46624C]">Optional live demo</p>
                    <h2 className="mt-1 text-xl font-black tracking-[-0.02em]">
                      Experience the product yourself
                    </h2>
                    <p className="mt-2 leading-7 text-[#59615C]">
                      Choose any supported language and speak with Chicky for up to five minutes.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openLiveDemo}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#46624C] bg-white px-5 font-black text-[#33483A] transition hover:-translate-y-0.5 hover:bg-[#F8FBF8] active:translate-y-px focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#46624C]/25"
                  >
                    Try ChickyTutor live
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>
            )}

            {stepId === "demo" && showLiveDemo && (
              <div className="mx-auto max-w-6xl">
                <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-sm font-black text-[#C05C2F]">Optional live demo</p>
                    <h1
                      ref={headingRef}
                      tabIndex={-1}
                      className="mt-2 max-w-[13ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                    >
                      Try ChickyTutor yourself.
                    </h1>
                    <p className="mt-4 max-w-2xl text-lg leading-8 text-[#5E625F]">
                      Pick a language, allow microphone access, and start speaking. Finish whenever
                      you like.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeLiveDemo}
                    className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#1C1C1E]/20 bg-white px-5 font-bold transition hover:border-[#C05C2F]/50 hover:text-[#9E431F] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FD954F]/25"
                  >
                    <ArrowLeft className="h-4 w-4" aria-hidden />
                    Return to onboarding
                  </button>
                </div>
                <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#0F0F14] shadow-[0_24px_60px_rgba(28,28,30,0.2)]">
                  <SpeakingDemoIframe
                    experimentVariant="affiliate_optional_live_demo"
                    loading="eager"
                    pageFamily="affiliate_program"
                    pagePath="/affiliateonboarding"
                    placement="affiliate_onboarding_optional_live_demo"
                    title="Optional live ChickyTutor speaking demo for affiliates"
                    onLoad={() => {
                      if (liveDemoLoadedRef.current) return;
                      liveDemoLoadedRef.current = true;
                      trackEventWithAttribution(posthog, "affiliate_recruitment_live_demo_loaded", {
                        placement: "affiliate_onboarding_demo_step",
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {stepId === "plan" && plan && (
              <div className="mx-auto max-w-5xl">
                <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
                  <div>
                    <MessageCircleMore className="h-10 w-10 text-[#C05C2F]" aria-hidden />
                    <h1
                      ref={headingRef}
                      tabIndex={-1}
                      className="mt-5 max-w-[11ch] text-4xl font-black leading-[1.02] tracking-[-0.045em] outline-none sm:text-5xl"
                    >
                      Start with this promotion plan.
                    </h1>
                    <p className="mt-5 max-w-xl text-lg leading-8 text-[#5E625F]">
                      This is a starting point for {plan.audienceLabel.toLowerCase()} on{" "}
                      {plan.channelLabel.toLowerCase()}.
                    </p>

                    <div className="mt-8 rounded-3xl bg-[#1C1C1E] p-6 text-white sm:p-8">
                      <p className="text-sm font-bold text-[#FD954F]">{plan.title}</p>
                      <p className="mt-4 text-2xl font-black leading-snug tracking-[-0.025em]">
                        “{plan.campaignLead}”
                      </p>
                      <p className="mt-5 leading-7 text-white/70">{plan.action}</p>
                    </div>

                    <div className="mt-5 border-l-2 border-[#C05C2F] pl-5">
                      <p className="font-black">Add a clear disclosure</p>
                      <p className="mt-2 leading-7 text-[#5E625F]">{plan.disclosure}</p>
                    </div>
                  </div>

                  <aside className="rounded-3xl border border-[#1C1C1E]/10 bg-[#E9EFE7] p-6 sm:p-8">
                    <p className="text-sm font-black text-[#46624C]">What you are joining</p>
                    <dl className="mt-6 grid grid-cols-2 gap-x-5 gap-y-7">
                      <div>
                        <dt className="text-sm text-[#626864]">Commission</dt>
                        <dd className="mt-1 text-3xl font-black tracking-[-0.04em]">
                          {AFFILIATE_PROGRAM.commissionPercent}%
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-[#626864]">Earning period</dt>
                        <dd className="mt-1 text-3xl font-black tracking-[-0.04em]">
                          {AFFILIATE_PROGRAM.commissionDurationMonths} mo.
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-[#626864]">Attribution</dt>
                        <dd className="mt-1 text-3xl font-black tracking-[-0.04em]">
                          {AFFILIATE_PROGRAM.attributionWindowDays} days
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-[#626864]">Threshold</dt>
                        <dd className="mt-1 text-3xl font-black tracking-[-0.04em]">
                          €{AFFILIATE_PROGRAM.payoutThresholdEur}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-7 border-t border-[#1C1C1E]/10 pt-5 text-sm leading-6 text-[#59615C]">
                      Commission is based on net collected subscription revenue. Payout setup opens
                      when the provider connection is ready.
                    </p>
                    <a
                      href={DASHBOARD_HREF}
                      onClick={() => {
                        trackEventWithAttribution(
                          posthog,
                          "affiliate_recruitment_onboarding_completed",
                          {
                            audience_id: audienceId,
                            channel_id: channelId,
                          }
                        );
                        trackEventWithAttribution(posthog, "affiliate_program_cta_clicked", {
                          placement: "affiliate_onboarding_final",
                          signup_mode: "self_service",
                          commission_percent: AFFILIATE_PROGRAM.commissionPercent,
                        });
                      }}
                      className={`${primaryButtonClassName} mt-7 w-full`}
                    >
                      Create affiliate account
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </a>
                    <Link
                      href="/affiliate/terms"
                      className="mt-4 flex min-h-10 items-center justify-center text-sm font-bold text-[#4D5A52] underline decoration-[#4D5A52]/35 underline-offset-4 hover:text-[#1C1C1E]"
                    >
                      Read program terms
                    </Link>
                  </aside>
                </div>
              </div>
            )}
          </motion.section>
        </AnimatePresence>

        {stepId !== "welcome" && stepId !== "plan" && !showLiveDemo && (
          <div
            className={`mx-auto flex max-w-5xl items-center gap-4 border-t border-[#1C1C1E]/10 pt-5 ${
              stepId === "demo" ? "justify-between" : "justify-start"
            }`}
          >
            <button
              type="button"
              onClick={goBack}
              className="inline-flex min-h-12 items-center gap-2 rounded-full px-3 font-bold text-[#5E625F] transition hover:bg-white hover:text-[#1C1C1E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C05C2F]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
            {stepId === "demo" && (
              <button type="button" onClick={goForward} className={primaryButtonClassName}>
                Continue to my plan
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        )}

        {stepId === "plan" && (
          <div className="mx-auto mt-2 flex max-w-5xl justify-start border-t border-[#1C1C1E]/10 pt-5">
            <button
              type="button"
              onClick={restart}
              className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-sm font-bold text-[#5E625F] hover:bg-white hover:text-[#1C1C1E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C05C2F]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Start again
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
